import { Component, ElementRef, Output, EventEmitter, Input, AfterViewInit, Renderer2, HostListener, OnDestroy } from '@angular/core';
import { AppUtility } from '../../utils/utility';

@Component({
    selector: 'al-table-options',
    templateUrl: './al-table-options.component.html'
})
export class AlTableOptionsComponent implements AfterViewInit, OnDestroy {

    private resizeTimeout;

    public FIRE_AFTER = 500;

    public isDropdownHidden: boolean = true;

    public documentClickListener;

    // INPUTS
    @Input() columns: any = [];

    @Input() excludeColumns: any = [];

    // OUTPUTS ( EventEmitters )
    @Output() onColumnShown: EventEmitter<any> = new EventEmitter();

    @Output() onColumnHidden: EventEmitter<any> = new EventEmitter();

    @Output() onCheckboxClicked: EventEmitter<any> = new EventEmitter();

    constructor(
        private el: ElementRef,
        private renderer: Renderer2,
        private appUtility: AppUtility
    ) { }

    // window.resize event listener
    @HostListener('window:resize', ['$event'])
    onWindowResize(event) {
        // clear the timeout
        clearTimeout(this.resizeTimeout);

        // Throttle/Debounce resize event to reduce continusly firing resize event
        // start timing for event "completion"
        this.resizeTimeout = setTimeout(() => {
            this.hideColumnsInResponsiveView(event);
        }, this.FIRE_AFTER);

    }

    ngAfterViewInit() {
        this.excludeColumnsFromHiding();
        this.hideColumnsInResponsiveView();

        // Hide dropdown on outside click like on document click or
        // on click of any element other then dropdown or checkbox
        this.documentClickListener = this.renderer.listen('document', 'click', (event) => {
            let elem = this.appUtility.findClosest(event.target, 'BY_CLASS', 'ui-multiselect');

            if (!elem) {
                this.isDropdownHidden = true;
            }
        });
    }

    /**
     * Toogle (show/hide) options dropdown menu
     * @param event
     */
    public toggleTableOptionsDropdown(event) {
        this.isDropdownHidden = !this.isDropdownHidden;
    }

    /**
     * Exclude columns from hiding on click of checkbox,
     * Requires column index to be provided like [excludeColumns]="[1,3]"
     * Checkbox will be disabled according to provided column index
     */
    public excludeColumnsFromHiding() {
        let chkboxList = this.el.nativeElement.querySelectorAll('input[type="checkbox"]');

        if (this.excludeColumns && this.excludeColumns.length > 0) {
            [].forEach.call(chkboxList, (checkbox, index) => {
                this.excludeColumns.forEach(value => {
                    if (index === value) {
                        this.renderer.setAttribute(checkbox, 'disabled', 'true');
                    }
                });
            });
        }
    }

    /**
     * Show column on click of Checkbox, if checked then show column
     * @param event Checkbox checked and unchecked event
     * @param colIndex Index of column which needs to be shown
     */
    public showColumn(event, colIndex: number) {
        let columns = this.el.nativeElement.querySelectorAll('table th:nth-child(' + colIndex + '), table td:nth-child(' + colIndex + ')');

        [].forEach.call(columns, col => {
            col.classList.remove('hidden');
        });

        this.onColumnShown.emit({ event, colIndex, columns, message: 'Show column event emitted' });
    }

    /**
     * Show all columns of the table
     * @param event
     */
    public showAllColumn(event) {
        for (let i = 0; i < this.columns.length; i++) {
            this.showColumn(event, (i + 1));

            // Update checkbox checked status accordingly
            this.updateCheckbox(i, true);
        }
    }

    /**
     * Hide table columns on click of checkbox, if un-checked then hide column
     * @param event Checkbox checked and unchecked event
     * @param colIndex Index of column which needs to be shown
     */
    public hideColumn(event, colIndex: number) {
        let columns = this.el.nativeElement.querySelectorAll('table th:nth-child(' + colIndex + '), table td:nth-child(' + colIndex + ')');

        [].forEach.call(columns, col => {
            col.classList.add('hidden');
        });

        this.onColumnHidden.emit({ event, colIndex, columns, message: 'Hide column event emitted' });
    }

    /**
     * Execute Show/Hide table columns functions on click of checkbox
     * @param event Checkbox checked and unchecked event
     */
    public onToggleColumn(event) {
        let selectedCheckboxValue = event.target.value;
        let colIndex = Number(selectedCheckboxValue) + 1;

        if (event.target.checked) {
            // If checkbox is checked, then show column
            this.showColumn(event, colIndex);
        } else {
            // If checkbox is un-checked, then hide column
            this.hideColumn(event, colIndex);
        }

        this.onCheckboxClicked.emit({ event, colIndex });
    }

    /**
     * Toggle show/hide number of columns on window resize and in responsive view
     * @param event Default Event
     * @param visibleColumnsUpto Number of columns which should be visible in responsive view, default all columns will be visible
     * @param shouldHide Boolean
     */
    public toggleColumnsUpto(event, visibleColumnsUpto: number, shouldHide: boolean) {
        if (shouldHide) {
            for (let i = this.columns.length; i > visibleColumnsUpto; i--) {
                this.hideColumn(event, i);
                this.updateCheckbox((i - 1), false);
            }
        } else {
            this.showAllColumn(event);
        }
    }


    /**
     * Update  checkbox's checked and unchecked status
     * @param chkboxIndex Provide index of checkbox which need to be updated
     * @param shouldChecked boolean Do you want to check it, then pass true
     */
    public updateCheckbox(chkboxIndex: number, shouldChecked: boolean) {
        let chkboxList = this.el.nativeElement.querySelectorAll('input[type="checkbox"]');
        this.renderer.setProperty(chkboxList[chkboxIndex], 'checked', shouldChecked);
    }


    /**
     * Toggle column visibility ( hide / Show ) in responsive view and also on window resize event
     * @param event Window resize event
     */
    public hideColumnsInResponsiveView(event?) {
        let htmlEle = document.querySelector('html');

        // Default number of visible columns is, lenth of columns or All columns will be visible by default
        let visibleColumnsUpto = this.columns.length;
        let shouldHideColumn = false;

        if (htmlEle.classList.contains('al-view-lt960') && htmlEle.classList.contains('al-view-gt720')) {
            shouldHideColumn = true;
            visibleColumnsUpto = 3;
        } else if (htmlEle.classList.contains('al-view-gt960')) {
            shouldHideColumn = false;
            visibleColumnsUpto = this.columns.length;
        } else if (htmlEle.classList.contains('al-view-lt480')) {
            shouldHideColumn = true;
            visibleColumnsUpto = 2;
        } else if (htmlEle.classList.contains('al-view-lt720')) {
            shouldHideColumn = true;
            visibleColumnsUpto = 3;
        }

        this.showAllColumn(event);
        this.toggleColumnsUpto(event, visibleColumnsUpto, shouldHideColumn);
    }


    ngOnDestroy() {
        // Remove event listener from browser document node
        if (this.documentClickListener) {
            this.documentClickListener();
        }
    }

}
